<template>
  <NavBar v-if="userLoaded" pageName="Service MVNO Operations"></NavBar>

  <v-container style="margin-top: 185px;">
    <!-- Loading Overlay -->
    <v-overlay :model-value="loading" class="align-center justify-center">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-btn variant="text" size="small" @click="$router.push('/settings/services')" class="mb-2">
      <v-icon size="small" class="mr-1">mdi-arrow-left</v-icon>
      Services Settings
    </v-btn>

    <!-- Tabs -->
    <v-tabs v-model="activeTab" color="primary" class="mb-4">
      <v-tab value="operations">
        <v-icon class="mr-2">mdi-cog</v-icon>
        Service MVNO Operations
        <v-chip size="x-small" class="ml-2" variant="tonal">{{ operations.length }}</v-chip>
      </v-tab>
      <v-tab value="operation-types">
        <v-icon class="mr-2">mdi-tag-multiple</v-icon>
        Operation Types
        <v-chip size="x-small" class="ml-2" variant="tonal">{{ operationTypes.length }}</v-chip>
      </v-tab>
      <v-tab value="api-methods">
        <v-icon class="mr-2">mdi-api</v-icon>
        MVNO API Methods
        <v-chip size="x-small" class="ml-2" variant="tonal">{{ mvnoApiMethods.length }}</v-chip>
      </v-tab>
    </v-tabs>

    <v-window v-model="activeTab">
      <!-- ==================== TAB 1: Service MVNO Operations ==================== -->
      <v-window-item value="operations">
        <v-row class="align-center mb-4">
          <v-col cols="12" md="6">
            <p class="text-caption text-grey">Configure which MVNO API to call for each service operation type</p>
          </v-col>
          <v-col cols="12" md="6" class="d-flex justify-end gap-2">
            <v-btn color="primary" variant="outlined" :loading="loading" @click="loadData">
              <v-icon class="mr-2">mdi-refresh</v-icon>
              Refresh
            </v-btn>
            <v-btn color="success" @click="openAddDialog">
              <v-icon class="mr-2">mdi-plus</v-icon>
              Add Operation
            </v-btn>
          </v-col>
        </v-row>

        <v-card>
          <v-card-text>
            <v-data-table
              :headers="tableHeaders"
              :items="operations"
              :loading="loading"
              :items-per-page="20"
              item-value="id"
              class="elevation-1"
            >
              <template #[`item.service_name`]="{ item }">
                <div>
                  <strong>{{ item.service_name }}</strong>
                  <div class="text-caption text-grey">ID: {{ item.service_id }}</div>
                </div>
              </template>
              <template #[`item.operation_type_name`]="{ item }">
                <v-chip
                  :color="item.operation_type_color || 'grey'"
                  size="small"
                  style="cursor: pointer;"
                  @click="activeTab = 'operation-types'"
                  title="Go to Operation Types"
                >
                  {{ item.operation_type_label || item.operation_type_name || '-' }}
                  <v-icon size="x-small" class="ml-1">mdi-open-in-new</v-icon>
                </v-chip>
              </template>
              <template #[`item.mvno_api_method_name`]="{ item }">
                <v-chip
                  color="info"
                  size="small"
                  variant="outlined"
                  style="cursor: pointer;"
                  @click="activeTab = 'api-methods'"
                  title="Go to MVNO API Methods"
                >
                  {{ item.mvno_api_method_description || item.mvno_api_method_name || '-' }}
                  <v-icon size="x-small" class="ml-1">mdi-open-in-new</v-icon>
                </v-chip>
              </template>
              <template #[`item.api_params`]="{ item }">
                <div v-if="item.api_params && Object.keys(item.api_params).length > 0" class="text-caption">
                  <div v-if="item.api_params.credit_source">
                    <v-icon size="x-small">mdi-currency-eur</v-icon>
                    {{ formatParamSource('credit', item.api_params) }}
                  </div>
                  <div v-if="item.api_params.valability_source">
                    <v-icon size="x-small">mdi-calendar</v-icon>
                    {{ formatParamSource('valability', item.api_params) }}
                  </div>
                </div>
                <span v-else class="text-grey text-caption">Default</span>
              </template>
              <template #[`item.active`]="{ item }">
                <v-chip
                  :color="item.active ? 'success' : 'error'"
                  size="small"
                  @click="toggleActive(item)"
                  style="cursor: pointer;"
                >
                  {{ item.active ? 'Active' : 'Inactive' }}
                </v-chip>
              </template>
              <template #[`item.actions`]="{ item }">
                <v-btn size="x-small" color="primary" class="mr-1" @click="openEditDialog(item)" title="Edit operation">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn size="x-small" color="error" @click="confirmDelete(item)" title="Delete operation">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
              <template v-slot:no-data>
                <v-alert type="info" class="ma-2">No service MVNO operations configured</v-alert>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <v-alert type="info" density="compact" class="mt-4">
          <v-icon class="mr-2">mdi-information</v-icon>
          These configurations define which <strong>MVNO API method</strong> to call for each service operation.
          Click on <strong>Operation Type</strong> or <strong>API Method</strong> chips to manage them.
        </v-alert>
      </v-window-item>

      <!-- ==================== TAB 2: Operation Types ==================== -->
      <v-window-item value="operation-types">
        <v-row class="align-center mb-4">
          <v-col cols="12" md="6">
            <p class="text-caption text-grey">Manage operation types used in MVNO operations</p>
          </v-col>
          <v-col cols="12" md="6" class="d-flex justify-end gap-2">
            <v-btn color="primary" variant="outlined" :loading="loading" @click="loadData">
              <v-icon class="mr-2">mdi-refresh</v-icon>
              Refresh
            </v-btn>
            <v-btn color="success" @click="openAddTypeDialog">
              <v-icon class="mr-2">mdi-plus</v-icon>
              Add Type
            </v-btn>
          </v-col>
        </v-row>

        <v-card>
          <v-card-text>
            <v-data-table
              :headers="typeTableHeaders"
              :items="operationTypes"
              :loading="loadingTypes"
              :items-per-page="10"
              item-value="id"
              density="compact"
              class="elevation-1"
            >
              <template #[`item.name`]="{ item }">
                <code>{{ item.name }}</code>
              </template>
              <template #[`item.label`]="{ item }">
                {{ item.label || '-' }}
              </template>
              <template #[`item.color`]="{ item }">
                <v-chip :color="item.color || 'grey'" size="small">
                  {{ item.color || 'grey' }}
                </v-chip>
              </template>
              <template #[`item.operations_count`]="{ item }">
                <v-chip
                  size="x-small"
                  :color="item.operations_count > 0 ? 'primary' : 'grey'"
                  variant="outlined"
                  :style="item.operations_count > 0 ? 'cursor: pointer;' : ''"
                  @click="item.operations_count > 0 ? activeTab = 'operations' : null"
                  :title="item.operations_count > 0 ? 'View operations' : ''"
                >
                  {{ item.operations_count || 0 }}
                </v-chip>
              </template>
              <template #[`item.actions`]="{ item }">
                <v-btn size="x-small" color="primary" class="mr-1" @click="openEditTypeDialog(item)" title="Edit type">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn size="x-small" color="error" @click="confirmDeleteType(item)" title="Delete type">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
              <template v-slot:no-data>
                <v-alert type="info" class="ma-2">No operation types defined</v-alert>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-window-item>

      <!-- ==================== TAB 3: MVNO API Methods ==================== -->
      <v-window-item value="api-methods">
        <v-row class="align-center mb-4">
          <v-col cols="12" md="6">
            <p class="text-caption text-grey">Manage MVNO API methods available for operations</p>
          </v-col>
          <v-col cols="12" md="6" class="d-flex justify-end gap-2">
            <v-btn color="primary" variant="outlined" :loading="loading" @click="loadData">
              <v-icon class="mr-2">mdi-refresh</v-icon>
              Refresh
            </v-btn>
            <v-btn color="success" @click="openAddMethodDialog">
              <v-icon class="mr-2">mdi-plus</v-icon>
              Add Method
            </v-btn>
          </v-col>
        </v-row>

        <v-card>
          <v-card-text>
            <v-data-table
              :headers="methodTableHeaders"
              :items="mvnoApiMethods"
              :loading="loadingMethods"
              :items-per-page="10"
              item-value="id"
              density="compact"
              class="elevation-1"
            >
              <template #[`item.name`]="{ item }">
                <code>{{ item.name }}</code>
              </template>
              <template #[`item.description`]="{ item }">
                {{ item.description || '-' }}
              </template>
              <template #[`item.operations_count`]="{ item }">
                <v-chip
                  size="x-small"
                  :color="item.operations_count > 0 ? 'primary' : 'grey'"
                  variant="outlined"
                  :style="item.operations_count > 0 ? 'cursor: pointer;' : ''"
                  @click="item.operations_count > 0 ? activeTab = 'operations' : null"
                  :title="item.operations_count > 0 ? 'View operations' : ''"
                >
                  {{ item.operations_count || 0 }}
                </v-chip>
              </template>
              <template #[`item.actions`]="{ item }">
                <v-btn size="x-small" color="primary" class="mr-1" @click="openEditMethodDialog(item)" title="Edit method">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn size="x-small" color="error" @click="confirmDeleteMethod(item)" title="Delete method">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
              <template v-slot:no-data>
                <v-alert type="info" class="ma-2">No MVNO API methods defined</v-alert>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-window-item>
    </v-window>

    <!-- Add/Edit Operation Dialog (subcomponent) -->
    <ServiceMvnoOperationDialog
      :show="formDialog"
      :is-editing="isEditing"
      :form-data="formData"
      :services="services"
      :operation-types="operationTypes"
      :mvno-api-methods="mvnoApiMethods"
      :saving="saving"
      @close="formDialog = false"
      @save="saveOperation"
    />

    <!-- Add/Edit Operation Type Dialog (subcomponent) -->
    <ServiceOperationTypeDialog
      :show="typeFormDialog"
      :is-editing="isEditingType"
      :form-data="typeFormData"
      :saving="savingType"
      @close="typeFormDialog = false"
      @save="saveOperationType"
    />

    <!-- Add/Edit MVNO API Method Dialog (subcomponent) -->
    <MvnoApiMethodDialog
      :show="methodFormDialog"
      :is-editing="isEditingMethod"
      :form-data="methodFormData"
      :saving="savingMethod"
      @close="methodFormDialog = false"
      @save="saveApiMethod"
    />

    <!-- Delete Operation Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="bg-error text-white">
          <v-icon class="mr-2">mdi-delete</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text class="pt-4">
          Are you sure you want to delete this operation?
          <div class="mt-2" v-if="itemToDelete">
            <strong>Service:</strong> {{ itemToDelete.service_name }}<br>
            <strong>Operation:</strong> {{ itemToDelete.operation_type_label || itemToDelete.operation_type_name }}<br>
            <strong>API Method:</strong> {{ itemToDelete.mvno_api_method_description || itemToDelete.mvno_api_method_name }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="deleteOperation" :loading="deleting">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Operation Type Confirmation Dialog -->
    <v-dialog v-model="deleteTypeDialog" max-width="400">
      <v-card>
        <v-card-title class="bg-error text-white">
          <v-icon class="mr-2">mdi-delete</v-icon>
          Confirm Delete Type
        </v-card-title>
        <v-card-text class="pt-4">
          Are you sure you want to delete this operation type?
          <div class="mt-2" v-if="typeToDelete">
            <strong>Name:</strong> {{ typeToDelete.name }}<br>
            <strong>Label:</strong> {{ typeToDelete.label || '-' }}<br>
            <strong>Used by:</strong> {{ typeToDelete.operations_count || 0 }} operations
          </div>
          <v-alert v-if="typeToDelete && typeToDelete.operations_count > 0" type="warning" density="compact" class="mt-2">
            This type is used by {{ typeToDelete.operations_count }} operations and cannot be deleted.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="deleteTypeDialog = false">Cancel</v-btn>
          <v-btn
            color="error"
            @click="deleteOperationType"
            :loading="deletingType"
            :disabled="typeToDelete && typeToDelete.operations_count > 0"
          >Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete MVNO API Method Confirmation Dialog -->
    <v-dialog v-model="deleteMethodDialog" max-width="400">
      <v-card>
        <v-card-title class="bg-error text-white">
          <v-icon class="mr-2">mdi-delete</v-icon>
          Confirm Delete Method
        </v-card-title>
        <v-card-text class="pt-4">
          Are you sure you want to delete this MVNO API method?
          <div class="mt-2" v-if="methodToDelete">
            <strong>Name:</strong> {{ methodToDelete.name }}<br>
            <strong>Description:</strong> {{ methodToDelete.description || '-' }}<br>
            <strong>Used by:</strong> {{ methodToDelete.operations_count || 0 }} operations
          </div>
          <v-alert v-if="methodToDelete && methodToDelete.operations_count > 0" type="warning" density="compact" class="mt-2">
            This method is used by {{ methodToDelete.operations_count }} operations and cannot be deleted.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="deleteMethodDialog = false">Cancel</v-btn>
          <v-btn
            color="error"
            @click="deleteApiMethod"
            :loading="deletingMethod"
            :disabled="methodToDelete && methodToDelete.operations_count > 0"
          >Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import NavBar from "@/components/NavBar.vue";
import ServiceMvnoOperationDialog from "@/components/dialogs/ServiceMvnoOperationDialog.vue";
import ServiceOperationTypeDialog from "@/components/dialogs/ServiceOperationTypeDialog.vue";
import MvnoApiMethodDialog from "@/components/dialogs/MvnoApiMethodDialog.vue";
import useGetLoggedIn from "@/composables/useGetLoggedIn";
import {
  getServiceMvnoOperations,
  addServiceMvnoOperation,
  updateServiceMvnoOperation,
  deleteServiceMvnoOperation
} from "@/composables/useServiceMvnoOperationsAPI";
import {
  addServiceOperationType,
  updateServiceOperationType,
  deleteServiceOperationType
} from "@/composables/useServiceOperationTypesAPI";
import {
  addMvnoApiMethod,
  updateMvnoApiMethod,
  deleteMvnoApiMethod
} from "@/composables/useMvnoApiMethodsAPI";
import { getServicesForSelect } from "@/composables/useDistributorServiceProductsAPI";
import { log } from "@/composables/useLog";
import store from "@/store";

export default defineComponent({
  name: 'ServiceMvnoOperationsSettings',
  components: {
    NavBar,
    ServiceMvnoOperationDialog,
    ServiceOperationTypeDialog,
    MvnoApiMethodDialog
  },
  data() {
    return {
      activeTab: 'operations',
      loading: false,
      loadingTypes: false,
      loadingMethods: false,
      token: '',
      userLoaded: false,
      operations: [],
      services: [],
      operationTypes: [],
      mvnoApiMethods: [],

      // Operation Dialog
      formDialog: false,
      isEditing: false,
      saving: false,
      formData: {
        id: null,
        service_id: null,
        service_operation_type_id: null,
        mvno_api_method_id: null,
        api_params: null,
        description: '',
        active: true
      },

      // Operation Type Dialog
      typeFormDialog: false,
      isEditingType: false,
      savingType: false,
      typeFormData: { id: null, name: '', label: '', color: 'grey' },

      // MVNO API Method Dialog
      methodFormDialog: false,
      isEditingMethod: false,
      savingMethod: false,
      methodFormData: { id: null, name: '', description: '' },

      // Delete operation dialog
      deleteDialog: false,
      deleting: false,
      itemToDelete: null,

      // Delete type dialog
      deleteTypeDialog: false,
      deletingType: false,
      typeToDelete: null,

      // Delete method dialog
      deleteMethodDialog: false,
      deletingMethod: false,
      methodToDelete: null,

      // Snackbar
      snackbar: { show: false, message: '', color: 'success' },

      // Table headers
      tableHeaders: [
        { title: 'ID', key: 'id', width: '5%' },
        { title: 'Service', key: 'service_name', width: '25%' },
        { title: 'Operation', key: 'operation_type_name', width: '15%' },
        { title: 'MVNO API Method', key: 'mvno_api_method_name', width: '18%' },
        { title: 'Params', key: 'api_params', width: '12%' },
        { title: 'Status', key: 'active', align: 'center', width: '10%' },
        { title: 'Actions', key: 'actions', sortable: false, width: '12%' }
      ],
      typeTableHeaders: [
        { title: 'ID', key: 'id', width: '8%' },
        { title: 'Name', key: 'name', width: '22%' },
        { title: 'Label', key: 'label', width: '22%' },
        { title: 'Color', key: 'color', width: '15%' },
        { title: 'Ops', key: 'operations_count', width: '10%' },
        { title: 'Actions', key: 'actions', sortable: false, width: '15%' }
      ],
      methodTableHeaders: [
        { title: 'ID', key: 'id', width: '8%' },
        { title: 'Name', key: 'name', width: '30%' },
        { title: 'Description', key: 'description', width: '30%' },
        { title: 'Ops', key: 'operations_count', width: '10%' },
        { title: 'Actions', key: 'actions', sortable: false, width: '15%' }
      ]
    }
  },
  methods: {
    showSnackbar(message, color = 'success') {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },

    formatParamSource(param, apiParams) {
      const source = apiParams[`${param}_source`];
      const value = apiParams[`${param}_value`];
      if (source === 'fixed') {
        return `Fixed: ${value}${param === 'valability' ? ' days' : ''}`;
      } else if (source === 'none') {
        return 'Skip';
      }
      return source;
    },

    // ==================== Data Loading ====================

    async loadData() {
      this.loading = true;
      try {
        const [operationsResult, servicesResult] = await Promise.all([
          getServiceMvnoOperations(this.token),
          getServicesForSelect(this.token)
        ]);

        log("Operations loaded:", operationsResult);

        if (!operationsResult.error) {
          this.operations = operationsResult.operations || [];
          this.operationTypes = operationsResult.operation_types || [];
          this.mvnoApiMethods = operationsResult.mvno_api_methods || [];
        } else {
          this.showSnackbar(operationsResult.message || 'Error loading operations', 'error');
        }

        if (!servicesResult.error) {
          this.services = servicesResult.services || [];
        }
      } catch (err) {
        log("Error loading data:", err);
        this.showSnackbar('Error loading data', 'error');
      } finally {
        this.loading = false;
      }
    },

    // ==================== Operation CRUD ====================

    openAddDialog() {
      this.isEditing = false;
      this.formData = {
        id: null, service_id: null, service_operation_type_id: null,
        mvno_api_method_id: null, api_params: null, description: '', active: true
      };
      this.formDialog = true;
    },

    openEditDialog(item) {
      this.isEditing = true;
      this.formData = {
        id: item.id, service_id: item.service_id,
        service_operation_type_id: item.service_operation_type_id,
        mvno_api_method_id: item.mvno_api_method_id,
        api_params: item.api_params,
        description: item.description || '', active: item.active
      };
      this.formDialog = true;
    },

    async saveOperation(data) {
      this.saving = true;
      try {
        let result;
        if (this.isEditing) {
          result = await updateServiceMvnoOperation(this.token, data);
        } else {
          result = await addServiceMvnoOperation(this.token, data);
        }
        if (!result.error) {
          this.showSnackbar(this.isEditing ? 'Operation updated successfully' : 'Operation added successfully');
          this.formDialog = false;
          await this.loadData();
        } else {
          this.showSnackbar(result.message || 'Error saving operation', 'error');
        }
      } catch (err) {
        log("Error saving operation:", err);
        this.showSnackbar('Error saving operation', 'error');
      } finally {
        this.saving = false;
      }
    },

    async toggleActive(item) {
      this.loading = true;
      try {
        const result = await updateServiceMvnoOperation(this.token, { id: item.id, active: !item.active });
        if (!result.error) {
          this.showSnackbar(`Operation ${!item.active ? 'activated' : 'deactivated'} successfully`);
          await this.loadData();
        } else {
          this.showSnackbar(result.message || 'Error updating operation', 'error');
        }
      } catch (err) {
        log("Error toggling active status:", err);
        this.showSnackbar('Error updating operation', 'error');
      } finally {
        this.loading = false;
      }
    },

    confirmDelete(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },

    async deleteOperation() {
      if (!this.itemToDelete) return;
      this.deleting = true;
      try {
        const result = await deleteServiceMvnoOperation(this.token, this.itemToDelete.id);
        if (!result.error) {
          this.showSnackbar('Operation deleted successfully');
          this.deleteDialog = false;
          this.itemToDelete = null;
          await this.loadData();
        } else {
          this.showSnackbar(result.message || 'Error deleting operation', 'error');
        }
      } catch (err) {
        log("Error deleting operation:", err);
        this.showSnackbar('Error deleting operation', 'error');
      } finally {
        this.deleting = false;
      }
    },

    // ==================== Operation Type CRUD ====================

    openAddTypeDialog() {
      this.isEditingType = false;
      this.typeFormData = { id: null, name: '', label: '', color: 'grey' };
      this.typeFormDialog = true;
    },

    openEditTypeDialog(item) {
      this.isEditingType = true;
      this.typeFormData = { id: item.id, name: item.name, label: item.label || '', color: item.color || 'grey' };
      this.typeFormDialog = true;
    },

    async saveOperationType(data) {
      this.savingType = true;
      try {
        let result;
        if (this.isEditingType) {
          result = await updateServiceOperationType(this.token, data);
        } else {
          result = await addServiceOperationType(this.token, data);
        }
        if (!result.error) {
          this.showSnackbar(this.isEditingType ? 'Operation type updated' : 'Operation type added');
          this.typeFormDialog = false;
          await this.loadData();
        } else {
          this.showSnackbar(result.message || 'Error saving operation type', 'error');
        }
      } catch (err) {
        log("Error saving operation type:", err);
        this.showSnackbar('Error saving operation type', 'error');
      } finally {
        this.savingType = false;
      }
    },

    confirmDeleteType(item) {
      this.typeToDelete = item;
      this.deleteTypeDialog = true;
    },

    async deleteOperationType() {
      if (!this.typeToDelete) return;
      this.deletingType = true;
      try {
        const result = await deleteServiceOperationType(this.token, this.typeToDelete.id);
        if (!result.error) {
          this.showSnackbar('Operation type deleted');
          this.deleteTypeDialog = false;
          this.typeToDelete = null;
          await this.loadData();
        } else {
          this.showSnackbar(result.message || 'Error deleting operation type', 'error');
        }
      } catch (err) {
        log("Error deleting operation type:", err);
        this.showSnackbar('Error deleting operation type', 'error');
      } finally {
        this.deletingType = false;
      }
    },

    // ==================== MVNO API Method CRUD ====================

    openAddMethodDialog() {
      this.isEditingMethod = false;
      this.methodFormData = { id: null, name: '', description: '' };
      this.methodFormDialog = true;
    },

    openEditMethodDialog(item) {
      this.isEditingMethod = true;
      this.methodFormData = { id: item.id, name: item.name, description: item.description || '' };
      this.methodFormDialog = true;
    },

    async saveApiMethod(data) {
      this.savingMethod = true;
      try {
        let result;
        if (this.isEditingMethod) {
          result = await updateMvnoApiMethod(this.token, data);
        } else {
          result = await addMvnoApiMethod(this.token, data);
        }
        if (!result.error) {
          this.showSnackbar(this.isEditingMethod ? 'MVNO API method updated' : 'MVNO API method added');
          this.methodFormDialog = false;
          await this.loadData();
        } else {
          this.showSnackbar(result.message || 'Error saving MVNO API method', 'error');
        }
      } catch (err) {
        log("Error saving MVNO API method:", err);
        this.showSnackbar('Error saving MVNO API method', 'error');
      } finally {
        this.savingMethod = false;
      }
    },

    confirmDeleteMethod(item) {
      this.methodToDelete = item;
      this.deleteMethodDialog = true;
    },

    async deleteApiMethod() {
      if (!this.methodToDelete) return;
      this.deletingMethod = true;
      try {
        const result = await deleteMvnoApiMethod(this.token, this.methodToDelete.id);
        if (!result.error) {
          this.showSnackbar('MVNO API method deleted');
          this.deleteMethodDialog = false;
          this.methodToDelete = null;
          await this.loadData();
        } else {
          this.showSnackbar(result.message || 'Error deleting MVNO API method', 'error');
        }
      } catch (err) {
        log("Error deleting MVNO API method:", err);
        this.showSnackbar('Error deleting MVNO API method', 'error');
      } finally {
        this.deletingMethod = false;
      }
    }
  },

  async created() {
    const { getLoggedIn } = useGetLoggedIn();
    await getLoggedIn();
    this.token = store.getters.getToken;
    this.userLoaded = true;
    await this.loadData();
  }
});
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
</style>
